import { useTheme } from 'next-themes';
import { useEffect, useState, useRef } from 'react';
import { useMediaQuery } from 'react-responsive';
import { Bump } from '@nivo/bump';

const BumpChart = ({ data, chartHeight = 300 }) => {
    const chartRef = useRef(null);
    const { theme } = useTheme();
    const [mounted, setMounted] = useState(false);
    const [chartDimensions, setChartDimensions] = useState({ width: 100, height: chartHeight });
    
    const isMobile = useMediaQuery({ maxWidth: 640 });
    const isTablet = useMediaQuery({ minWidth: 641, maxWidth: 1024 });
    const isLargeDesktop = useMediaQuery({ minWidth: 1440 });
    const isExtraLargeDesktop = useMediaQuery({ minWidth: 1920 });

    useEffect(() => {
        setMounted(true);
    }, []);

    useEffect(() => {
        if (!mounted || !chartRef.current) return;
        
        const updateChartDimensions = () => {
            if (chartRef.current) {
                const { width } = chartRef.current.getBoundingClientRect();
                
                let height = chartHeight;
                
                // Responsive height adjustments
                if (isMobile) {
                    height = Math.max(250, Math.min(300, chartHeight * 0.8)); // Smaller on mobile
                } else if (isTablet) {
                    height = Math.max(300, Math.min(350, chartHeight));
                } else if (isExtraLargeDesktop && width > 800) {
                    height = Math.min(450, chartHeight * 1.3);
                } else if (isLargeDesktop && width > 600) {
                    height = Math.min(400, chartHeight * 1.2);
                }
                
                setChartDimensions({
                    width: Math.max(width - 8, 100), // Account for padding
                    height: height
                });
            }
        };

        updateChartDimensions();
        
        const resizeObserver = new ResizeObserver(updateChartDimensions);
        resizeObserver.observe(chartRef.current);
        
        // Also listen to window resize for media query changes
        const handleResize = () => {
            setTimeout(updateChartDimensions, 100);
        };
        
        window.addEventListener('resize', handleResize);
        
        return () => {
            if (chartRef.current) {
                resizeObserver.unobserve(chartRef.current);
            }
            window.removeEventListener('resize', handleResize);
        };
    }, [mounted, chartHeight, isMobile, isTablet, isLargeDesktop, isExtraLargeDesktop]);

    // Theme configuration for Nivo chart
    const getChartTheme = () => {
        const isDark = theme === 'dark';

        return {
            background: 'transparent',
            text: {
                fontSize: 12,
                fill: isDark ? '#ffffff' : '#000000', // chart text
                fontFamily: 'inherit'
            },
            axis: {
                domain: {
                    line: {
                        stroke: isDark ? '#ffffff' : '#d1d5db',
                        strokeWidth: 1
                    }
                },
                legend: {
                    text: {
                        fontSize: 12,
                        fill: isDark ? '#ffffff' : '#374151', // Responsive axis legend colors
                        fontFamily: 'inherit',
                        fontWeight: 'bold'
                    }
                },
                ticks: {
                    line: {
                        stroke: isDark ? '#ffffff' : '#d1d5db',
                        strokeWidth: 1
                    },
                    text: {
                        fontSize: 11,
                        fill: isDark ? '#ffffff' : '#374151', // Responsive tick label colors
                        fontFamily: 'inherit',
                        fontWeight: '500'
                    }
                }
            },
            grid: {
                line: {
                    stroke: isDark ? '#4b5563' : '#f3f4f6',
                    strokeWidth: 1
                }
            },
            crosshair: {
                line: {
                    stroke: isDark ? '#9ca3af' : '#9ca3af',
                    strokeWidth: 1,
                    strokeOpacity: 0.75
                }
            }
        };
    };

    const getMargins = () => {
        if (isMobile) {
            return { top: 10, right: 15, bottom: 45, left: 30 }; // Optimized mobile margins
        } else if (isTablet) {
            return { top: 15, right: 40, bottom: 50, left: 40 };
        } else if (isLargeDesktop) {
            return { top: 25, right: 80, bottom: 60, left: 50 };
        } else if (isExtraLargeDesktop) {
            return { top: 30, right: 120, bottom: 70, left: 60 };
        } else {
            return { top: 20, right: 60, bottom: 50, left: 40 };
        }
    };

    // Improved date formatting function
    const formatDateLabel = (value) => {
        if (!value) return '';
        
        const parts = value.split('/');
        if (parts.length !== 2) return value;
        
        // On mobile, show minimal format
        if (isMobile) {
            return `${parts[0]}/${parts[1]}`; // Keep full format but smaller
        }
        
        return `${parts[0]}/${parts[1]}`;
    };

const getAxisConfig = () => {
    const isDark = theme === 'dark';
    const textColor = isDark ? '#ffffff' : '#374151';

    const bottomAxis = {
        tickSize: isMobile ? 2 : 4,
        tickPadding: isMobile ? 2 : 4,
        tickRotation: isMobile ? -45 : -30,
        legend: '',
        legendPosition: 'middle',
        legendOffset: isMobile ? 35 : 40,
        format: formatDateLabel,
        tickValues: isMobile ? 'every 3 ticks' : 'every 1 tick',
    };

    const leftAxis = {
        tickSize: isMobile ? 2 : 4,
        tickPadding: isMobile ? 2 : 4,
        tickRotation: 0,
        legend: isMobile ? '' : 'Rank',
        legendPosition: 'middle',
        legendOffset: isMobile ? -25 : -30,
    };

    return { bottomAxis, leftAxis };
};


    // Filter legend based on screen size
    const getLegends = () => {
        const isDark = theme === 'dark';
        
        // On mobile, don't show legends at all to save space
        if (isMobile) {
            return [];
        }
        
        // On tablet, use a compact horizontal layout
        if (isTablet) {
            return [{
                anchor: 'bottom',
                direction: 'row',
                justify: false,
                translateY: 45,
                itemsSpacing: 5,
                itemWidth: 50,
                itemHeight: 16,
                itemDirection: 'left-to-right',
                itemTextColor: isDark ? '#e2e8f0' : '#64748b',
                symbolSize: 8,
                symbolShape: 'circle',
                effects: [
                    {
                        on: 'hover',
                        style: {
                            itemTextColor: isDark ? '#ffffff' : '#000000'
                        }
                    }
                ]
            }];
        }
        
        // Large desktop screens
        if (isLargeDesktop) {
            return [{
                anchor: 'right',
                direction: 'column',
                justify: false,
                translateX: 60,
                translateY: 0,
                itemsSpacing: 5,
                itemWidth: 100,
                itemHeight: 24,
                itemDirection: 'left-to-right',
                itemTextColor: isDark ? '#e2e8f0' : '#64748b',
                symbolSize: 14,
                symbolShape: 'circle',
                effects: [
                    {
                        on: 'hover',
                        style: {
                            itemTextColor: isDark ? '#ffffff' : '#000000'
                        }
                    }
                ]
            }];
        }
        
        // Extra large desktop screens
        if (isExtraLargeDesktop) {
            return [{
                anchor: 'right',
                direction: 'column',
                justify: false,
                translateX: 90,
                translateY: 0,
                itemsSpacing: 8,
                itemWidth: 120,
                itemHeight: 28,
                itemDirection: 'left-to-right',
                itemTextColor: isDark ? '#e2e8f0' : '#64748b',
                symbolSize: 16,
                symbolShape: 'circle',
                effects: [
                    {
                        on: 'hover',
                        style: {
                            itemTextColor: isDark ? '#ffffff' : '#000000'
                        }
                    }
                ]
            }];
        }
        
        // Default desktop legend
        return [{
            anchor: 'right',
            direction: 'column',
            justify: false,
            translateX: 40,
            translateY: 0,
            itemsSpacing: 2,
            itemWidth: 80,
            itemHeight: 18,
            itemDirection: 'left-to-right',
            itemTextColor: isDark ? '#ffffff' : '#64748b',
            symbolSize: 10,
            symbolShape: 'circle',
            effects: [
                {
                    on: 'hover',
                    style: {
                        itemTextColor: isDark ? '#ffffff' : '#000000'
                    }
                }
            ]
        }];
    };

    // Determine line and point sizes based on screen size
    const getChartElementSizes = () => {
        if (isMobile) {
            return {
                activeLineWidth: 2,
                inactiveLineWidth: 2,
                pointSize: 4,
                inactiveOpacity: 0.2,
                activePointSize: 6,
                inactivePointSize: 2,
                pointBorderWidth: 2,
                activePointBorderWidth: 2
            };
        } else if (isTablet) {
            return {
                activeLineWidth: 2,
                inactiveLineWidth: 2,
                pointSize: 4,
                inactiveOpacity: 0.15,
                activePointSize: 6,
                inactivePointSize: 1,
                pointBorderWidth: 2,
                activePointBorderWidth: 2
            };
        } else {
            return {
                activeLineWidth: 2,
                inactiveLineWidth: 3,
                pointSize: 3,
                inactiveOpacity: 0.15,
                activePointSize: 5,
                inactivePointSize: 0,
                pointBorderWidth: 3,
                activePointBorderWidth: 3
            };
        }
    };

    // If not mounted yet, return empty placeholder
    if (!mounted) {
        return <div ref={chartRef} className="w-full h-full min-h-[250px]"></div>;
    }

    const chartElementSizes = getChartElementSizes();
    const { bottomAxis, leftAxis } = getAxisConfig();

    return (
        <div ref={chartRef} className="w-full h-full overflow-hidden relative min-h-[250px]">
            {chartDimensions.width > 50 && chartDimensions.height > 100 && (
                <Bump
                    width={chartDimensions.width}
                    height={chartDimensions.height}
                    data={data}
                    theme={getChartTheme()}
                    colors={{ scheme: 'spectral' }}
                    activeLineWidth={chartElementSizes.activeLineWidth}
                    inactiveLineWidth={chartElementSizes.inactiveLineWidth}
                    inactiveOpacity={chartElementSizes.inactiveOpacity}
                    pointSize={chartElementSizes.pointSize}
                    activePointSize={chartElementSizes.activePointSize}
                    inactivePointSize={chartElementSizes.inactivePointSize}
                    pointColor={{ theme: 'background' }}
                    pointBorderWidth={chartElementSizes.pointBorderWidth}
                    activePointBorderWidth={chartElementSizes.activePointBorderWidth}
                    pointBorderColor={{ from: 'color' }}
                    enableGridX={false}
                    enableGridY={!isMobile} // Disable Y grid on mobile for cleaner look
                    axisTop={null}
                    axisRight={null}
                    axisBottom={bottomAxis}
                    axisLeft={leftAxis}
                    margin={getMargins()}
                    legends={getLegends()}
                    animate={false}
                />
            )}
        </div>
    );
};

export default BumpChart;